/**
 * TierWelcome (T-173-001 + T-173-002, US-173 / F-203).
 *
 * Post-upgrade celebration screen: dependency-free confetti (T-173-001),
 * an updated tier badge showing the newly paid plan (T-173-002), and a
 * short welcome body with a dashboard CTA. Reached at /texas/welcome?tier=…
 * (routed from the upgrade surface once the entitlement refresh confirms).
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Confetti from '../Shared/Confetti';
import { getTierLabel } from '../../services/subscriptionEntitlement';

export const TierWelcome: React.FC = () => {
    const [searchParams] = useSearchParams();
    const tier = searchParams.get('tier') || searchParams.get('plan');
    const display = getTierLabel(tier);

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="tier-welcome">
            <Confetti />
            <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full p-8 md:p-10 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-rocket text-emerald-600 text-2xl" data-testid="tier-welcome-icon"></i>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">You're all set!</h1>
                <p className="text-sm text-gray-500 mt-1">Payment confirmed — your plan is live.</p>

                <span
                    data-testid="tier-welcome-badge"
                    className="inline-flex items-center mt-5 px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm"
                >
                    <i className="fas fa-crown mr-2 text-indigo-600"></i>
                    {display} plan active
                </span>

                <p className="text-sm text-gray-600 mt-5 leading-relaxed">
                    {display === 'Enterprise'
                        ? 'Evidence Safe, Smart Document Reader, and every premium tool are unlocked.'
                        : 'Your upgraded features are unlocked and ready to use.'}
                </p>

                <ul className="mt-5 text-left space-y-2 text-sm text-gray-700 max-w-sm mx-auto" data-testid="tier-welcome-benefits">
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Tier gates lifted — no more upgrade prompts.
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Latest compliance tools available on your dashboard.
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Billing details confirmed on your subscription tab.
                    </li>
                </ul>

                <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to="/dashboard"
                        data-testid="tier-welcome-dashboard"
                        className="flex-1 bg-indigo-600 text-white px-6 py-2.5 rounded-lg font-semibold text-center hover:bg-indigo-700"
                    >
                        Go to dashboard
                    </Link>
                    <Link
                        to="/texas/evidence"
                        data-testid="tier-welcome-evidence"
                        className="flex-1 px-6 py-2.5 rounded-lg font-medium text-indigo-700 border border-indigo-200 hover:bg-indigo-50 text-center"
                    >
                        Open Evidence Safe
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TierWelcome;